import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
}

interface EditMemberProps {
  user: User;
  onClose: () => void;
  onSave: (updatedUser: User) => void;
  onDelete: (userId: number) => void;
}

const EditMember: React.FC<EditMemberProps> = ({
  user,
  onClose,
  onSave,
  onDelete,
}) => {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [status, setStatus] = useState(user.status);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSave = () => {
    onSave({ ...user, name, role, status });
    onClose();
  };

  const handleDelete = () => {
    onDelete(user.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div className="bg-white rounded-lg relative w-96">
        <div className="flex w-full items-center justify-between p-3">
          <h2 className="text-lg font-bold pl-3">Edit Member</h2>
          <button onClick={onClose} className="text-3xl">
            &times;
          </button>
        </div>

        <form className="pl-6 pr-6 ">
          <div className="mb-4">
            <label className="block text-sm mb-2">Name</label>
            <input
              type="text"
              className="w-full px-4 py-3 border rounded-lg text-sm"
              value={name}
              onChange={e => {
                if (e.target.value.length <= 50) {
                  setName(e.target.value);
                }
              }}
            />
            <div className="text-[12px] text-gray-500 mt-1">
              {name.length}/50
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm mb-2">Email</label>
            <input
              type="email"
              className="w-full px-4 py-3 border rounded-lg text-sm bg-gray focus:outline-none"
              value={user.email}
              readOnly
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm mb-2">Role</label>
            <span className="flex">
              <label className="w-1/2">
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={role === "admin"}
                  onChange={e => setRole(e.target.value)}
                  className="accent-black"
                />{" "}
                Admin
              </label>
              <label>
                <input
                  type="radio"
                  name="role"
                  value="forecaster"
                  checked={role === "forecaster"}
                  onChange={e => setRole(e.target.value)}
                  className="accent-black"
                />{" "}
                Forecaster
              </label>
            </span>
          </div>

          {user.status !== "Pending" && (
            <div className="mb-4">
              <label className="block text-sm mb-2">Status</label>
              <div className="relative" ref={dropdownRef}>
                <div
                  className="w-full px-4 py-3 border rounded-lg text-sm bg-white flex justify-between items-center cursor-pointer"
                  onClick={() => setDropdownOpen(prev => !prev)}
                >
                  {status}
                  <DropdownArrow
                    className={`w-3 h-3 transition-transform ${
                      dropdownOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </div>

                {dropdownOpen && (
                  <div className="absolute w-full  bg-white border rounded-lg shadow-md z-10 font-poppins text-sm">
                    <div
                      className={`${status === "Active" ? "bg-blue-500 text-white" : ""} px-4 py-2 cursor-pointer  font-poppins text-sm rounded-md`}
                      onClick={() => {
                        setStatus("Active");
                        setDropdownOpen(false);
                      }}
                    >
                      Active
                    </div>
                    <div
                      className={`${status === "Disabled" ? "bg-blue-500 text-white" : ""} px-4 py-2 cursor-pointer  font-poppins text-sm rounded-md`}
                      onClick={() => {
                        setStatus("Disabled");
                        setDropdownOpen(false);
                      }}
                    >
                      Disabled
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        <div className="flex justify-center gap-2 shadow-top-md">
          <button
            className="w-[35%] font-bold text-sm px-8 py-3 rounded-full border-gray border-2 m-4"
            onClick={handleDelete}
          >
            Delete
          </button>
          <button
            className="w-[35%] font-bold text-sm bg-black text-white px-8 py-3 rounded-full m-4"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMember;
